import dayjs from "dayjs";
import { request } from "@/common/api";
import { Loading } from "@/components/atoms/Loading";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { useUserInfo } from "@/common/hooks/useUserInfo";
import { UserInfoBox } from "@/components/molecules/UserInfoBox";
import { MainContent } from "@/components/organisms/MainContent";
import { MainTemplate } from "@/components/templates/MainTemplate";
import { exerciseStart } from "@/common/functions/flutter";
import { UserHealthInfo } from "@/components/molecules/UserHealthInfo";
import { ExerciseRecomBox } from "@/components/molecules/ExerciseRecomBox";
import { useSetRecoilState } from "recoil";
import { guardianUserState } from "@/common/states/user";
import { useEffect, useState } from "react";
import { CommentFloatMessage } from "@/components/atoms/CommentFloatMessage";
import HealthData, { HealthSummaryResponse } from "@/common/types/healthData";
import {
  Guardian,
  ContinuousAttendanceLog,
  ExercisePointEligibility,
} from "@/common/types/student";
import {
  GET_GUARDIAN,
  GET_HEALTH_INFO,
  GET_UNREAD_MESSAGE,
  GET_CONTINUOUS_ATTENDANCE,
  GET_EXERCISE_AVAILABILITY,
  GET_STUDENT_HEALTH_SUMMARY,
} from "@/common/querykeys";
import { EventExercise } from "@/components/molecules/EventExercise";

export const MainPage = () => {
  /** 📦 State Store */
  const navigate = useNavigate();
  const setGuardianUser = useSetRecoilState(guardianUserState);
  const [modalShow, setModalShow] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [exerciseModalShow, setExerciseModalShow] = useState(false);
  const { student, guardian, loginType, studentId } = useUserInfo();
  const [continuousModalShow, setContinuousModalShow] = useState(true);
  const [isAutoAttendanceModal, setIsAutoAttendanceModal] = useState(false);
  const [latestAttendanceData, setLatestAttendanceData] = useState<
    ContinuousAttendanceLog["data"] | null
  >(null);

  /** 📡 API Layer */
  // 학생 건강 정보 요약 관련 데이터 조회 (🧑‍🎓)
  const {
    data: studentHealthSummary,
    isFetching: isStudentHealthSummaryFetching,
  } = useQuery<HealthSummaryResponse>({
    queryKey: [GET_STUDENT_HEALTH_SUMMARY],
    queryFn: async () => {
      return await request<any>({
        method: "GET",
        url: `/student-health/summary/${studentId}`,
      });
    },
    enabled: !!studentId && forceRefresh,
  });

  // 법정보호자 정보 관련 데이터 조회 (👨‍👩‍👧‍👦)
  const { data: guardianInfo } = useQuery<Guardian>({
    queryKey: [GET_GUARDIAN],
    queryFn: async () => {
      return await request<Guardian>({
        method: "GET",
        url: "/guardian/info",
      });
    },
    enabled: loginType === "guardian",
  });

  // 학생 건강 정보 관련 데이터 조회 (🧑‍🎓)
  const { data: hasUnread, isPending: hasUnreadFetching } = useQuery<boolean>({
    queryKey: [GET_UNREAD_MESSAGE],
    queryFn: async () => {
      return await request<boolean>({
        method: "GET",
        url: `/cheer-message/student/unread/check`,
      });
    },
    enabled: loginType === "student",
  });

  // 학생 건강 정보 관련 데이터 조회 (🧑‍🎓 + 👨‍👩‍👧‍👦)
  const { data: statusLevels } = useQuery<HealthData>({
    queryKey: [GET_HEALTH_INFO],
    queryFn: async () => {
      const studentId =
        loginType === "student" ? student?.id : guardian?.student?.id;

      return await request<HealthData>({
        method: "GET",
        url: `/physical-exam/my-health/${studentId}`,
      });
    },
    enabled: Boolean(guardianInfo?.student || studentId),
  });

  // 학생이 운동할 때 포인트 지급 가능 여부 조회 (🧑‍🎓)
  const { data: exercisePointEligibility } = useQuery<ExercisePointEligibility>(
    {
      queryKey: [GET_EXERCISE_AVAILABILITY],
      queryFn: async () => {
        const res = await request<ExercisePointEligibility[]>({
          method: "GET",
          url: `/student/video-point-eligibility/${studentId}`,
        });

        return res[0];
      },
      enabled: loginType === "student",
    }
  );

  // 연속 출석 요일에 대한 데이터 조회 (🧑‍🎓)
  const {
    data: continuousAttendanceLog,
    refetch: fetchContinuousAttendance,
    isSuccess: isAttendanceLoaded,
  } = useQuery<ContinuousAttendanceLog>({
    queryKey: [GET_CONTINUOUS_ATTENDANCE, studentId],
    queryFn: async () => {
      const res = await request<ContinuousAttendanceLog>({
        method: "GET",
        url: `/student/get-continuous-attendance-log/${studentId}`,
      });
      return res;
    },
    enabled:
      !!studentId && !isStudentHealthSummaryFetching && !!studentHealthSummary,
    staleTime: 0,
    refetchOnMount: "always",
  });

  /** 🗺️ Event Handlers */
  // 사진 활용 동의 페이지로 이동
  const handleCameraClick = () => {
    navigate("/spine/photo-agree");
  };

  // 내 건강 정보 관리 페이지로 이동
  const handleHealthInfoClick = () => {
    navigate("/setting/health-info");
  };

  const handleExerciseConfirm = (result: boolean) => {
    if (result) {
      exerciseStart(student?.id, studentHealthSummary?.video?.videoId, true);
    } else {
    }
  };

  const handleOpenAttendanceModal = async () => {
    setIsAutoAttendanceModal(false);
    if (loginType === "student" && studentId) {
      const result = await fetchContinuousAttendance();
      if (result?.data?.data) {
        setLatestAttendanceData(result.data.data);
        setContinuousModalShow(true);
      }
    }
  };

  /** 🔄 Side Effect Management */
  useEffect(() => {
    if (studentId && !forceRefresh) {
      setForceRefresh(true);
    }
  }, [forceRefresh, studentId]);

  useEffect(() => {
    if (loginType === "guardian" && guardianInfo) {
      setGuardianUser(guardianInfo);
    }
  }, [guardianInfo, loginType, setGuardianUser]);

  useEffect(() => {
    const today = dayjs().format("YYYY-MM-DD");
    const hideAttendanceDate = localStorage.getItem(
      "HIDE_ATTENDANCE_MODAL_DATE"
    );

    const loadAndOpenModal = async () => {
      if (hideAttendanceDate !== today) {
        setIsAutoAttendanceModal(true);
        if (loginType === "student" && studentId) {
          const result = await fetchContinuousAttendance();
          if (result?.data) {
            setContinuousModalShow(true);
          }
        }
      } else {
        setContinuousModalShow(false);
      }

      setIsInitialized(true);

      setTimeout(() => {
        const hideEventDate = localStorage.getItem("HIDE_MODAL_DATE");
        if (hideEventDate !== today) {
          setModalShow(true);
        }
      }, 100);
    };

    loadAndOpenModal();
  }, [loginType, studentId]);

  useEffect(() => {
    if (loginType === "student" && studentId) {
      fetchContinuousAttendance();
    }
  }, [loginType, studentId, fetchContinuousAttendance]);

  useEffect(() => {
    if (!continuousModalShow && isAttendanceLoaded) {
      const today = dayjs().format("YYYY-MM-DD");
      const hideEventDate = localStorage.getItem("HIDE_MODAL_DATE");
      const hideAttendanceDate = localStorage.getItem(
        "HIDE_ATTENDANCE_MODAL_DATE"
      );

      if (hideAttendanceDate === today || !isAutoAttendanceModal) return;

      if (hideEventDate !== today) {
        setModalShow(true);
      }
    }
  }, [continuousModalShow, isAttendanceLoaded, isAutoAttendanceModal]);

  return (
    <>
      <MainTemplate
        nav={true}
        subPage={true}
        menuBtn={true}
        // calenderBtn={!!studentId}
        pageTitle="홈"
        onCalendarClick={handleOpenAttendanceModal}
      >
        {isStudentHealthSummaryFetching ? (
          <Loading />
        ) : (
          <MainContent
            contentItem={
              <>
                {/* 👨‍👩‍👧‍👦 및 🧑‍🎓 정보 */}
                <UserInfoBox
                  loginType={loginType}
                  guardian={guardianInfo ?? guardian}
                  student={student}
                />
                {/* 내 건강 요약 */}
                <UserHealthInfo
                  key={`${studentHealthSummary?.health?.spine?.analysisResult?.probability}-${studentHealthSummary?.health?.physicalMeasurements?.bmi}-${studentHealthSummary?.health?.physicalMeasurements?.bodyFatPercentage}`}
                  title={loginType}
                  photoData={
                    !!studentHealthSummary?.health?.spine?.analysisResult
                      ?.hasCondition
                  }
                  statusLevels={statusLevels}
                  healthData={
                    !!studentHealthSummary?.health?.physicalMeasurements?.bmi
                  }
                  healthMetrics={{
                    spineAngle: studentHealthSummary?.health?.spine
                      ?.analysisResult.probability
                      ? Math.floor(
                          studentHealthSummary?.health?.spine?.analysisResult
                            .probability * 10
                        ) / 10
                      : 0,
                    bmi:
                      studentHealthSummary?.health?.physicalMeasurements?.bmi ||
                      0,
                    bodyFatPercentage:
                      studentHealthSummary?.health?.physicalMeasurements
                        ?.bodyFatPercentage || 0,
                  }}
                  onCameraClick={handleCameraClick}
                  handleHealthInfoClick={handleHealthInfoClick}
                  studentData={
                    loginType === "guardian"
                      ? !!guardianInfo?.student?.id
                      : !!studentId
                  }
                />
                <div style={{ padding: "20px 0 20px 0" }}>
                  <EventExercise
                    exercisePointEligibility={exercisePointEligibility}
                    exerciseCount={Number(
                      exercisePointEligibility?.exerciseCount
                    )}
                  />
                </div>
                {/* 주간 운동목표 - 학생과 보호자 모두 표시
              {studentId && (
                <ExerciseStateItem type="state" data={exerciseInfo} />
              )}
              <NoticeBanner>
                유튜브 정책 변경으로 인해
                <br />
                현재 영상이 정상적으로 재생되지 않습니다.
                <br />
                불편을 드려 죄송하며, 조속히 처리하겠습니다.
              </NoticeBanner> */}
              </>
            }
            commentFloat={hasUnread && !hasUnreadFetching ? true : false}
          >
            {/* 오늘의 운동 추천 (🧑‍🎓) */}
            {loginType === "student" &&
              student?.id &&
              studentHealthSummary?.video?.videoId && (
                <ExerciseRecomBox
                  type="main"
                  title={studentHealthSummary?.video?.title}
                  thumbnailUrl={studentHealthSummary?.video?.thumbnailUrl}
                  duration={studentHealthSummary?.video?.duration}
                  onClick={() => {
                    // if (
                    //   Number(exercisePointEligibility?.exerciseCount) === 36 ||
                    //   exercisePointEligibility?.points === 50000
                    // ) {
                    //   setExerciseModalShow(true);
                    // } else {
                    //   exerciseStart(
                    //     student?.id,
                    //     studentHealthSummary?.video?.videoId,
                    //     true
                    //   );
                    // }
                    exerciseStart(
                      student?.id,
                      studentHealthSummary?.video?.videoId,
                      true
                    );
                  }}
                >
                  {/* 추후 다시 살릴 가능성 배제 할 수 없으므로 제거 X */}
                  {/* <ExerciseGoal
                    main={true}
                    exerciseData={
                      isStudentHealthSummaryFetching
                        ? undefined
                        : studentHealthSummary?.exercise
                    }
                    type="home"
                  /> */}
                </ExerciseRecomBox>
              )}
            {/* 보호자 회원 응원메시지가 있는 경우 표기 */}
            {hasUnread && !hasUnreadFetching && <CommentFloatMessage />}
          </MainContent>
        )}
      </MainTemplate>

      {/* 이벤트와 같은 이미지 배너 모달 */}
      {/* {isInitialized && (
        <>
          <>
            {modalShow ? (
              <ModalEvent
                setModalShow={setModalShow}
                loginType={loginType === "student" ? "student" : "guardian"}
              />
            ) : exerciseModalShow ? (
              exercisePointEligibility?.points === 50000 &&
              Number(exercisePointEligibility?.exerciseCount) === 36 ? (
                <ModalExerciseConfirm
                  setModalShow={setExerciseModalShow}
                  onConfirm={handleExerciseConfirm}
                  type="all"
                />
              ) : exercisePointEligibility?.points === 50000 ? (
                <ModalExerciseConfirm
                  setModalShow={setExerciseModalShow}
                  onConfirm={handleExerciseConfirm}
                  type="point"
                />
              ) : (
                <ModalExerciseConfirm
                  setModalShow={setExerciseModalShow}
                  onConfirm={handleExerciseConfirm}
                  type="exercise"
                />
              )
            ) : null}
          </>
        </>
      )} */}
    </>
  );
};
